import * as v from 'valibot';

/**
 * Matching a trigger against the *contents* of an event payload.
 *
 * The `(source, event, matchKey)` index answers "which workflows care about
 * this kind of event" in one query, and it has to stay a small closed set of
 * precomputed keys to do that (see `triggerSubscriptions`). It cannot express
 * "only when `data.priority` is 1", because reef would have to enumerate every
 * field of every provider's payload at commit time to build the keys.
 *
 * So there are two layers, and they are deliberately different:
 *
 * | Layer | Where | Cost |
 * |-------|-------|------|
 * | event + match keys | indexed D1 lookup | one query for all workflows |
 * | payload conditions | in the router, per matched workflow | one pass over a parsed object |
 *
 * The index narrows a delivery to the handful of workflows that subscribe to
 * that event; conditions then decide, per workflow, whether the payload is one
 * it actually wanted. A workflow with conditions and no event narrowing still
 * costs the index nothing, because the conditions never reach the database.
 */

/** Longest regex reef will run against an untrusted payload. */
const MAX_PATTERN_LENGTH = 200;

/** Longest string a regex condition is tested against, in characters. */
const MAX_SUBJECT_LENGTH = 8192;

export const PayloadMatchOperator = v.picklist([
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'in',
  'not_in',
  'exists',
  'missing',
  'matches',
  'gt',
  'gte',
  'lt',
  'lte',
]);
export type PayloadMatchOperator = v.InferOutput<typeof PayloadMatchOperator>;

/**
 * One condition on one path through the event payload.
 *
 * The path is dotted, with two extras: a numeric segment indexes an array
 * (`labels.0.name`), and `*` fans out over every element of an array or every
 * value of an object (`pull_request.labels.*.name`).
 */
export const PayloadMatch = v.object({
  /** Dotted path into the payload, e.g. `data.priority` or `labels.*.name`. */
  path: v.pipe(v.string(), v.minLength(1)),
  op: PayloadMatchOperator,
  /**
   * The comparison value. Required by every operator except `exists` and
   * `missing`, which are about presence rather than content. `in`/`not_in`
   * take an array; everything else takes a scalar.
   */
  value: v.optional(v.unknown()),
  /**
   * String comparisons fold case by default, because the things people match on
   * — label names, states, branch names — are typed by hand in someone else's
   * product and arrive however they were typed. Set true where case is
   * meaningful.
   */
  caseSensitive: v.optional(v.boolean()),
});
export type PayloadMatch = v.InferOutput<typeof PayloadMatch>;

/** Whether every condition has to hold, or just one. */
export const PayloadMatchMode = v.picklist(['all', 'any']);
export type PayloadMatchMode = v.InferOutput<typeof PayloadMatchMode>;

/**
 * Read every value a path selects.
 *
 * Returns a list because `*` fans out: `labels.*.name` over three labels is
 * three values. A path that selects nothing returns an empty list, which is
 * what `exists` and `missing` are asking about.
 */
export function readPath(payload: unknown, path: string): unknown[] {
  let cursor: unknown[] = [payload];

  for (const segment of path.split('.')) {
    if (segment === '') continue;
    const next: unknown[] = [];

    for (const value of cursor) {
      if (value === null || value === undefined) continue;

      if (segment === '*') {
        if (Array.isArray(value)) next.push(...value);
        else if (typeof value === 'object') next.push(...Object.values(value as object));
        continue;
      }

      if (Array.isArray(value)) {
        const index = Number.parseInt(segment, 10);
        if (Number.isInteger(index) && index >= 0 && index < value.length) next.push(value[index]);
        continue;
      }

      if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        // `in` rather than a truthiness check: `{ merged: false }` has the key,
        // and `exists` has to say so.
        if (segment in record) next.push(record[segment]);
      }
    }

    cursor = next;
    if (cursor.length === 0) return [];
  }

  return cursor;
}

/**
 * Evaluate one condition against a payload.
 *
 * **Negative operators are the negation of the whole set, not "any value that
 * differs".** `labels.*.name not_equals 'wontfix'` means *no* label is
 * `wontfix` — the reading someone writing a filter intends. Under "any value"
 * semantics an issue labelled `bug` and `wontfix` would pass a filter written
 * to exclude exactly it.
 */
export function evaluateMatch(payload: unknown, condition: PayloadMatch): boolean {
  const values = readPath(payload, condition.path);

  switch (condition.op) {
    case 'exists':
      return values.some((value) => value !== null && value !== undefined);
    case 'missing':
      return !values.some((value) => value !== null && value !== undefined);
    case 'not_equals':
      return !values.some((value) => equals(value, condition.value, condition.caseSensitive));
    case 'not_contains':
      return !values.some((value) => contains(value, condition.value, condition.caseSensitive));
    case 'not_in':
      return !values.some((value) => oneOf(value, condition.value, condition.caseSensitive));
    case 'equals':
      return values.some((value) => equals(value, condition.value, condition.caseSensitive));
    case 'contains':
      return values.some((value) => contains(value, condition.value, condition.caseSensitive));
    case 'in':
      return values.some((value) => oneOf(value, condition.value, condition.caseSensitive));
    case 'matches':
      return values.some((value) => matchesPattern(value, condition.value, condition.caseSensitive));
    case 'gt':
      return values.some((value) => compare(value, condition.value, (a, b) => a > b));
    case 'gte':
      return values.some((value) => compare(value, condition.value, (a, b) => a >= b));
    case 'lt':
      return values.some((value) => compare(value, condition.value, (a, b) => a < b));
    case 'lte':
      return values.some((value) => compare(value, condition.value, (a, b) => a <= b));
  }
}

/**
 * Does this payload satisfy a trigger's conditions?
 *
 * No conditions means yes: a trigger that narrows only by event still fires on
 * every delivery of that event, which is the behaviour every workflow written
 * before conditions existed already relies on.
 */
export function matchesPayload(
  payload: unknown,
  conditions: readonly PayloadMatch[] | undefined,
  mode: PayloadMatchMode = 'all',
): boolean {
  if (!conditions?.length) return true;
  return mode === 'any'
    ? conditions.some((condition) => evaluateMatch(payload, condition))
    : conditions.every((condition) => evaluateMatch(payload, condition));
}

/**
 * Reasons a condition can never do what its author meant.
 *
 * Reported at commit time rather than at delivery time: a filter that silently
 * never matches looks exactly like an event that never arrived, and the two are
 * debugged very differently.
 */
export function validatePayloadMatch(condition: PayloadMatch): string[] {
  const issues: string[] = [];
  const needsValue = condition.op !== 'exists' && condition.op !== 'missing';

  if (needsValue && condition.value === undefined) {
    issues.push(`operator "${condition.op}" needs a value to compare against`);
  }
  if (!needsValue && condition.value !== undefined) {
    issues.push(`operator "${condition.op}" tests for presence and ignores its value`);
  }

  if ((condition.op === 'in' || condition.op === 'not_in') && !Array.isArray(condition.value)) {
    issues.push(`operator "${condition.op}" needs an array of values`);
  }

  if (condition.op === 'matches') {
    if (typeof condition.value !== 'string') {
      issues.push('operator "matches" needs a regular expression as a string');
    } else if (condition.value.length > MAX_PATTERN_LENGTH) {
      issues.push(`regular expression is longer than ${MAX_PATTERN_LENGTH} characters`);
    } else {
      try {
        new RegExp(condition.value);
      } catch (error) {
        issues.push((error as Error).message);
      }
    }
  }

  return issues;
}

// ---------------------------------------------------------------- operators

function normalize(value: unknown, caseSensitive: boolean | undefined): unknown {
  return typeof value === 'string' && !caseSensitive ? value.toLowerCase() : value;
}

function equals(actual: unknown, expected: unknown, caseSensitive?: boolean): boolean {
  const left = normalize(actual, caseSensitive);
  const right = normalize(expected, caseSensitive);
  if (left === right) return true;

  // A payload that carries `"2"` where the filter says `2` is the same event.
  // Numbers and booleans arrive as strings often enough — query strings, form
  // encodings, hand-written JSON — that refusing to bridge them would make the
  // filter wrong more often than strictness would make it right.
  if (
    (typeof left === 'number' || typeof left === 'boolean') &&
    typeof right === 'string'
  ) {
    return String(left) === right.toLowerCase();
  }
  if (
    typeof left === 'string' &&
    (typeof right === 'number' || typeof right === 'boolean')
  ) {
    return left === String(right).toLowerCase();
  }
  return false;
}

function contains(actual: unknown, expected: unknown, caseSensitive?: boolean): boolean {
  if (Array.isArray(actual)) {
    return actual.some((entry) => equals(entry, expected, caseSensitive));
  }
  if (typeof actual !== 'string' || typeof expected !== 'string') return false;
  return String(normalize(actual, caseSensitive)).includes(
    String(normalize(expected, caseSensitive)),
  );
}

function oneOf(actual: unknown, expected: unknown, caseSensitive?: boolean): boolean {
  if (!Array.isArray(expected)) return false;
  return expected.some((entry) => equals(actual, entry, caseSensitive));
}

function matchesPattern(actual: unknown, pattern: unknown, caseSensitive?: boolean): boolean {
  if (typeof actual !== 'string' || typeof pattern !== 'string') return false;
  if (pattern.length > MAX_PATTERN_LENGTH) return false;

  try {
    // The subject is attacker-influenced and the pattern is author-written, so
    // the bound that matters is on the input: a pathological pattern cannot be
    // fed an unbounded string to chew on.
    return new RegExp(pattern, caseSensitive ? '' : 'i').test(actual.slice(0, MAX_SUBJECT_LENGTH));
  } catch {
    return false;
  }
}

function compare(actual: unknown, expected: unknown, apply: (a: number, b: number) => boolean): boolean {
  const left = toNumber(actual);
  const right = toNumber(expected);
  if (left === null || right === null) return false;
  return apply(left, right);
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
